import { differenceInSeconds } from 'date-fns';
import { TimerAction, TimestampEntity } from '../timer/state/timer.interface';
import { DEFAULT_DAY_START_HOUR, isCurrentLogicalDay } from './day-key.utils';

export function calculateSessionTime(
  mode: TimerAction.Focus | TimerAction.Rest,
  entities: TimestampEntity[],
  dayStartHour: number = DEFAULT_DAY_START_HOUR,
) {
  if (entities.length === 0) {
    return 0;
  }

  // Flag to know if the current session being calculated is the same as the {mode};
  // Used to add time in case we find a Resume action
  let isTargetSession = false;

  const oppositeMode = mode === TimerAction.Rest ? TimerAction.Focus : TimerAction.Rest;

  let totalTime = entities.reduce((totalSeconds, entity, index) => {
    // An unterminated trailing entry only keeps accruing while its LOGICAL day is still in
    // progress. Calendar-today is not the same thing: at 09:00 with a 4am boundary, a 01:00 entry
    // belongs to yesterday and must not accrue to now.
    let compareDate: Date | null = isCurrentLogicalDay(entity.timestamp, dayStartHour)
      ? new Date()
      : null;

    if (entity.action === mode || (entity.action === TimerAction.Resume && isTargetSession)) {
      isTargetSession = true;
      if (index < entities.length - 1) {
        const nextEntry = entities[index + 1];
        if (
          nextEntry.action === TimerAction.Stop ||
          nextEntry.action === oppositeMode ||
          nextEntry.action === TimerAction.Pause
        ) {
          compareDate = new Date(entities[index + 1].timestamp);
        } else {
          // Cases where the user closes the tab while resting
          compareDate = null;
        }
      }

      if (compareDate) {
        totalSeconds += differenceInSeconds(compareDate, new Date(entity.timestamp));
      }
    } else if (entity.action === oppositeMode || entity.action === TimerAction.Stop) {
      isTargetSession = false;
    }
    return totalSeconds;
  }, 0);

  return totalTime;
}

export interface DayTotals {
  focus: number;
  rest: number;
  total: number;
}

/**
 * Focus + rest totals for one day's entries, including the unclaimed-rest bonus: when the
 * day's multiplier earned more rest (focus / multiplier) than was taken, the difference
 * counts as rest. These are the semantics the weekly view has always used —
 * WeeklyAnalyticsRepository.constructDayAnalyticsEntry now delegates here so the weekly
 * view and the weekly review can never disagree.
 */
export function calculateDayTotals(
  timestamps: TimestampEntity[],
  multiplier: number | null | undefined,
  dayStartHour: number = DEFAULT_DAY_START_HOUR,
): DayTotals {
  const focus = calculateSessionTime(TimerAction.Focus, timestamps, dayStartHour);
  let rest = calculateSessionTime(TimerAction.Rest, timestamps, dayStartHour);

  if (multiplier != null && focus / multiplier > rest) {
    rest += Math.round(focus / multiplier - rest);
  }

  return { focus, rest, total: focus + rest };
}
